import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

function tagsToString(tags) {
  return Array.isArray(tags) ? tags.join(", ") : "";
}

function stringToTags(value) {
  return String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function normalizeRowForForm(row) {
  return {
    activity: row.activity || "",
    category: row.category || "",
    unit: row.unit || "",
    time_per_unit:
      row.time_per_unit === null || row.time_per_unit === undefined
        ? ""
        : String(row.time_per_unit),
    tags: tagsToString(row.tags),
    is_active: !!row.is_active,
  };
}

export default function LabourLibraryPage() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const [newRow, setNewRow] = useState({
    activity: "",
    category: "",
    unit: "m2",
    time_per_unit: "",
    tags: "",
  });

  const saveTimersRef = useRef({});

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("labour_library")
      .select("*")
      .order("category", { ascending: true })
      .order("activity", { ascending: true });

    if (error) {
      console.error(error);
    } else {
      const nextRows = data || [];
      setRows(nextRows);

      const nextDrafts = {};
      nextRows.forEach((row) => {
        nextDrafts[row.id] = normalizeRowForForm(row);
      });
      setDrafts(nextDrafts);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchData();

    return () => {
      Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  async function saveRow(id) {
    const draft = drafts[id];
    if (!draft) return;

    setSavingRows((prev) => ({ ...prev, [id]: true }));
    setSaveStatus((prev) => ({ ...prev, [id]: "saving" }));

    const payload = {
      activity: draft.activity.trim(),
      category: draft.category.trim(),
      unit: draft.unit.trim(),
      time_per_unit: Number(draft.time_per_unit) || 0,
      tags: stringToTags(draft.tags),
      is_active: !!draft.is_active,
    };

    const { error } = await supabase
      .from("labour_library")
      .update(payload)
      .eq("id", id);

    if (error) {
      console.error(error);
      setSaveStatus((prev) => ({ ...prev, [id]: "error" }));
    } else {
      setRows((prev) =>
        prev.map((row) =>
          row.id === id
            ? {
                ...row,
                ...payload,
              }
            : row
        )
      );

      setSaveStatus((prev) => ({ ...prev, [id]: "saved" }));

      window.clearTimeout(saveTimersRef.current[id]);
      saveTimersRef.current[id] = window.setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [id]: "" }));
      }, 1200);
    }

    setSavingRows((prev) => ({ ...prev, [id]: false }));
  }

  function scheduleAutosave(id) {
    if (saveTimersRef.current[id]) {
      clearTimeout(saveTimersRef.current[id]);
    }

    setSaveStatus((prev) => ({ ...prev, [id]: "pending" }));

    saveTimersRef.current[id] = setTimeout(() => {
      saveRow(id);
    }, 700);
  }

  function updateDraft(id, field, value) {
    setDrafts((prev) => {
      const next = {
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      };
      return next;
    });

    scheduleAutosave(id);
  }

  async function createRow() {
    if (!newRow.activity.trim()) {
      alert("Activity is required");
      return;
    }

    const payload = {
      activity: newRow.activity.trim(),
      category: newRow.category.trim(),
      unit: newRow.unit.trim() || "m2",
      time_per_unit: Number(newRow.time_per_unit) || 0,
      tags: stringToTags(newRow.tags),
      is_active: true,
    };

    const { error } = await supabase.from("labour_library").insert([payload]);

    if (error) {
      console.error(error);
      return;
    }

    setNewRow({
      activity: "",
      category: "",
      unit: "m2",
      time_per_unit: "",
      tags: "",
    });

    fetchData();
  }

  const categories = useMemo(() => {
    const values = [...new Set(rows.map((row) => row.category).filter(Boolean))];
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows.filter((row) => {
      const draft = drafts[row.id] || normalizeRowForForm(row);

      const matchesSearch =
        !query ||
        [
          draft.activity,
          draft.category,
          draft.unit,
          draft.tags,
          draft.time_per_unit,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesCategory =
        categoryFilter === "all" || draft.category === categoryFilter;

      const matchesActive =
        activeFilter === "all" ||
        (activeFilter === "active" && draft.is_active) ||
        (activeFilter === "inactive" && !draft.is_active);

      return matchesSearch && matchesCategory && matchesActive;
    });
  }, [rows, drafts, search, categoryFilter, activeFilter]);

  function renderStatus(id) {
    const status = saveStatus[id];
    if (status === "saving") return "Saving...";
    if (status === "pending") return "Pending";
    if (status === "saved") return "Saved";
    if (status === "error") return "Error";
    return "";
  }

  return (
    <div style={{ padding: 20 }}>
      <h1>Labour Library</h1>

      <div
        style={{
          marginBottom: 20,
          padding: 12,
          border: "1px solid #ccc",
          borderRadius: 8,
        }}
      >
        <h3>Add new labour rule</h3>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <input
            type="text"
            placeholder="Activity"
            value={newRow.activity}
            onChange={(e) =>
              setNewRow({ ...newRow, activity: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Category"
            value={newRow.category}
            onChange={(e) =>
              setNewRow({ ...newRow, category: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Unit"
            value={newRow.unit}
            onChange={(e) =>
              setNewRow({ ...newRow, unit: e.target.value })
            }
          />

          <input
            type="number"
            step="0.01"
            placeholder="Time per unit"
            value={newRow.time_per_unit}
            onChange={(e) =>
              setNewRow({ ...newRow, time_per_unit: e.target.value })
            }
          />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={newRow.tags}
            onChange={(e) =>
              setNewRow({ ...newRow, tags: e.target.value })
            }
            style={{ minWidth: 400 }}
          />

          <button onClick={createRow}>Add</button>
        </div>
      </div>

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search activity, category, tags, unit..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 320 }}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>

        <button
          onClick={() => {
            setSearch("");
            setCategoryFilter("all");
            setActiveFilter("all");
          }}
        >
          Clear filters
        </button>

        <button onClick={fetchData}>Refresh</button>

        <div style={{ marginLeft: "auto", fontWeight: 600 }}>
          Showing {filteredRows.length} / {rows.length}
        </div>
      </div>

      {loading ? (
        <div>Loading labour library...</div>
      ) : (
        <table border="1" cellPadding="6" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Activity</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Time / Unit</th>
              <th>Tags</th>
              <th>Active</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((row) => {
              const draft = drafts[row.id] || normalizeRowForForm(row);

              return (
                <tr key={row.id}>
                  <td>
                    <input
                      type="text"
                      value={draft.activity}
                      onChange={(e) =>
                        updateDraft(row.id, "activity", e.target.value)
                      }
                      style={{ minWidth: 260 }}
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      value={draft.category}
                      onChange={(e) =>
                        updateDraft(row.id, "category", e.target.value)
                      }
                      style={{ minWidth: 180 }}
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      value={draft.unit}
                      onChange={(e) =>
                        updateDraft(row.id, "unit", e.target.value)
                      }
                      style={{ width: 80 }}
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.time_per_unit}
                      onChange={(e) =>
                        updateDraft(row.id, "time_per_unit", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      value={draft.tags}
                      onChange={(e) =>
                        updateDraft(row.id, "tags", e.target.value)
                      }
                      style={{ minWidth: 280 }}
                    />
                  </td>

                  <td>
                    <input
                      type="checkbox"
                      checked={!!draft.is_active}
                      onChange={(e) =>
                        updateDraft(row.id, "is_active", e.target.checked)
                      }
                    />
                  </td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    {savingRows[row.id] ? "Saving..." : renderStatus(row.id)}
                  </td>
                </tr>
              );
            })}

            {!filteredRows.length && (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: 20 }}>
                  No rows found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}